import { useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { cloneDeep } from "@apollo/client/utilities";
import {
  ExpenseReportAnalyticGroup,
  ExpenseStatus,
  GraphAnalyticInterval,
  useAdminReportGraphMetricsQuery,
} from "@common/utils/graphql";
import { TileHeader } from "../components/TileHeader";
import { DashboardTile } from "../components/DashboardTile";
import { Body } from "@hypercard/web-components";
import { IconBox } from "@common/components";
import { User } from "@phosphor-icons/react";

// Inclusive of today and 30 days ago
const now = new Date();
const endDay = new Date(
  now.getFullYear(),
  now.getMonth(),
  now.getDate(),
  23,
  59,
  59,
  999,
);
const startDay = new Date(
  now.getFullYear(),
  now.getMonth(),
  now.getDate() - 30,
  0,
  0,
  0,
  0,
);

export const SpenderDeptTile = () => {
  const { data: departmentExpenseRaw, loading: departmentExpenseLoading } =
    useAdminReportGraphMetricsQuery({
      variables: {
        groupedBy: ExpenseReportAnalyticGroup.Department,
        interval: GraphAnalyticInterval.Daily,
        filters: {
          transactedAfter: startDay,
          transactedBefore: endDay,
          expenseStatus: [ExpenseStatus.Approved],
        },
      },
    });

  const departmentExpenses = useMemo(() => {
    if (
      departmentExpenseRaw?.adminReportGraphMetrics?.__typename !==
      "QueryAdminReportGraphMetricsSuccess"
    ) {
      return [];
    }

    const expenses = cloneDeep(
      departmentExpenseRaw.adminReportGraphMetrics.data.metrics,
    );
    const totalSpend = expenses.reduce((acc, curr) => acc + curr.totalValue, 0);
    const sortedExpenses = expenses
      .sort((x, y) => {
        return y.totalValue - x.totalValue;
      })
      .slice(0, 6);
    return sortedExpenses.map((e) => ({
      name: e.groupedEntityName,
      percentage: `${((e.totalValue / totalSpend) * 100).toFixed(0)}%`,
    }));
  }, [departmentExpenseRaw]);

  return (
    <DashboardTile className="h-[300px] w-full">
      <TileHeader>SPENDERS BY DEPARTMENT (LAST 30 DAYS)</TileHeader>
      {departmentExpenseLoading ? (
        <>
          <Skeleton className="mt-4 h-[36px] w-[full] rounded-xl" />
          <Skeleton className="mt-2 h-[36px] w-[full] rounded-xl" />
          <Skeleton className="mt-2 h-[36px] w-[full] rounded-xl" />
          <Skeleton className="mt-2 h-[36px] w-[full] rounded-xl" />
          <Skeleton className="mt-2 h-[36px] w-[full] rounded-xl" />
        </>
      ) : departmentExpenses.length > 0 ? (
        <div className="flex h-full flex-col justify-center gap-2 pb-6">
          {departmentExpenses
            .map((e) => ({
              name: e.name,
              value: e.percentage,
            }))
            .map((val, i) => (
              <div
                className="flex flex-row justify-between"
                key={`${val.name}-${i}`}
              >
                <p>{val.name}</p>
                <div className="text-slate">{val.value}</div>
              </div>
            ))}
        </div>
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center pb-8">
          <IconBox>
            <User className="size-8" />
          </IconBox>
          <Body size="lg" weight="medium" className="mt-6">
            No data available
          </Body>
          <Body
            size="sm"
            weight="regular"
            className="whitespace-pre text-center text-white/70"
          >
            {
              "Submit reimbursements to see a\nbreakdown of spending by departments."
            }
          </Body>
        </div>
      )}
    </DashboardTile>
  );
};
